import React, { useEffect, useLayoutEffect, useRef, useMemo, useState, useCallback, useId } from "react";
import {
    Button,
    Col,
    Collapse,
    Container,
    DropdownMenu,
    DropdownToggle,
    Dropdown,
    DropdownItem,
    Form,
    FormGroup,
    Input,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
    Modal, 
    ModalBody,
    ModalHeader,
    ModalFooter,
    InputGroup,
    InputGroupText,
    Spinner
} from "reactstrap";

import { useTheme } from "../hooks/useTheme";
import { useSortData } from "../hooks/useSortData";
import { useSearchData } from "../hooks/useSearchData";
import { useFilterDataByField } from "../hooks/useFilterDataByField";
import { formatCurrency, formatNumber } from "../utils/numberUtils";
import { NumberInput } from "./DataInputs";

import DataLoaderFailed from "./loader/DataLoaderFailed";
import SortIcon from "./SortIcon";
import DataFilter from "./DataFilter";
import Download from "./Download";
import { useLockState } from "./userLocks/LockCheck";

/*
 * DataTableWindowed Component
 *
 * A performant, feature-rich data table with virtual scrolling for large datasets.
 * The parent passes the full dataset; when a fixed `height` (e.g. "600px") is supplied
 * only the visible rows (plus a buffer) are rendered, keeping the DOM small.
 *
 * ─────────────────────────────────────────────
 * COMPONENT PROPS
 * ─────────────────────────────────────────────
 *
 * id (string)
 *   Unique identifier for the table instance. Used as the key for persisting column
 *   visibility and search-field settings in localStorage.
 *
 * columns (array) — see COLUMN CONFIGURATION below
 *   Array of column definition objects that describe every column in the table.
 *
 * data (array)
 *   The full dataset. Each item should have an `id` property.
 *   Nested data is supported — if a row has an `items` array, it is rendered as an
 *   expandable/collapsible group with child rows indented beneath it.
 *
 * isLoading (boolean)
 *   When true, a loading skeleton is displayed instead of the table.
 *
 * error (any)
 *   When truthy, an error state is shown via <DataLoaderFailed />.
 *
 * height (string | null)  — default: null
 *   CSS height for the scroll container (e.g. "600px"). Enables virtual scrolling.
 * 
 * shrinkHeightToContents (boolean) — default: false
 *   When true and a height is set, the container will shrink to fit the content
 *   if the flattened data occupies less space than the specified height. Virtual
 *   scrolling is disabled in this case (all rows are rendered).
 *   When null the table renders all rows without a scroll container.
 *
 * searchableFields (string[])  — default: []
 *   Array of column keys that the built-in search bar can filter on.
 *   Only keys whose columns are currently visible are used.
 *
 * filters (array)  — default: []
 *   Array of advanced-filter definitions rendered via <DataFilter />.
 *   Each filter object: { id, label, type, options?, defaultValue, customIncludeFunction? }.
 *
 * onFilterChange (function)
 *   Callback fired when an advanced filter value changes.
 *   Signature: ({ filterId, value }) => void.
 *
 * actions (JSX[])  — default: []
 *   Array of React elements (typically Buttons) rendered in the toolbar.
 *
 * onRowClick (function)
 *   Callback fired when a data row is clicked.
 *   Signature: (rowData) => void.
 *
 * sortByColumn (string | null)  — default: null
 *   Column key for the initial sort. Falls back to the first visible column.
 *
 * sortDirection (string)  — default: "ascending"
 *   Initial sort direction. Values: "ascending" | "descending".
 *
 * footer (JSX | null)  — default: null
 *   Custom footer content rendered below the table body.
 *
 * showAutogeneratedFooter (boolean)  — default: false
 *   When true, an auto-generated footer row is rendered that totals columns
 *   marked with `canBeTotalled` and formats values using each column's `dataType`.
 *
 * exportToCsv (boolean)  — default: false
 *   When true, a CSV download button appears in the toolbar.
 *
 * csvFileName (string)  — default: "data.csv"
 *   File name used for the CSV export.
 *
 * allowColumnCustomisation (boolean)  — default: false
 *   When true, a settings button appears letting the user show/hide and fix/unfix
 *   columns. Visibility and fixed state are persisted in localStorage keyed by `id`.
 *
 * columnProfiles (object | undefined)
 *   Optional map of profile-key → column-config arrays for multi-profile column setups.
 *   When not supplied, a single profile is auto-generated from `columns`.
 *
 * currentProfileKey (string)  — default: "All"
 *   The active profile key when using `columnProfiles`.
 *
 * showGroupHeaders (boolean)  — default: true
 *   When true, columns with matching `group` values display a shared group header row.
 *
 * allowMultiSelect (boolean)  — default: false
 *   When true, a checkbox column is prepended, enabling row selection.
 *   Supports nested data — selecting a parent toggles all descendants.
 *   A "select all" checkbox in the header toggles all currently filtered rows.
 *
 * onSelectedDataChange (function)
 *   Callback fired when the set of selected rows changes (only when `allowMultiSelect`
 *   is enabled). Receives the array of currently selected, filtered leaf rows.
 *   Signature: (selectedRows) => void.
 *
 * onFilteredDataChange (function)
 *   Callback fired when the filtered dataset changes (after search + advanced filters).
 *   Signature: (filteredData) => void.
 *
 * ─────────────────────────────────────────────
 * COLUMN CONFIGURATION
 * ─────────────────────────────────────────────
 *
 * Each column object can have the following properties:
 *
 * REQUIRED:
 *   key (string)            Unique identifier; maps to the data property name.
 *
 * DISPLAY:
 *   label (string)          Display name used in headers and the column-settings dialog.
 *   header (string|JSX)     Custom header content. Falls back to `label`.
 *   align (string)          "left" (default) | "right". Affects header and cell alignment.
 *
 * STYLING:
 *   className (string)      CSS classes applied to <th> and <td>.
 *   style (object)          Inline styles for <th> and <td>. Use width/minWidth/maxWidth here.
 *
 * GROUPING (nested / grouped column headers):
 *   group (string)          Group label. Columns sharing a group are visually grouped.
 *   groupHeader (boolean)   If true, renders as a spanning header for grouped child rows.
 *   groupColumn (boolean)   If true, this column is spanned by a groupHeader on parent rows.
 *
 * FIXED COLUMNS (horizontal scrolling):
 *   isFixed (boolean)       When true the column stays pinned while the table scrolls horizontally.
 *   fixDirection (string)   "left" (default) | "right". Which edge to pin to.
 *   pinLocked (boolean)     When true, users cannot toggle pin/unpin for this column in settings.
 *
 * SORTING:
 *   isSortDisabled (boolean) If true, the column header is not clickable for sorting.
 *   sortValue (function)    Optional. Called instead of the raw cell value when sorting.
 *                           Signature: (value, row) => comparable. Mirrors `searchValue`.
 *
 * RENDERING:
 *   render (function)       Custom cell renderer.
 *                           Signature: (value, row, isExpanded, hasChildren) => JSX.
 *   calcColumn (function)   Derived-value function used instead of `row[key]`.
 *                           Signature: (row) => value.
 *
 * FOOTER:
 *   canBeTotalled (boolean) Include this column in auto-generated footer totals.
 *   showInFooter (boolean)  Explicitly show/hide the column in the footer.
 *   dataType (string)       Footer formatting: "currency" | "percentage" | "number" | "text".
 *   calcFooter (function)   Custom footer calculation. Signature: (footerData, allData) => value.
 *   renderFooter (function) Custom footer renderer. Signature: (value, footerData, allData) => JSX.
 *
 * COLUMN CUSTOMISATION (requires allowColumnCustomisation):
 *   isSystem (boolean)      If true, the column cannot be hidden (always required).
 *   isDefault (boolean)     If true, the column is visible by default.
 *   hideFromSettingsDialog (boolean)  If true, the column is hidden from the settings UI.
 *
 * DATA ROW PROPERTIES:
 *   id (any)                Each data row should have an `id` for selection and keying.
 *   items (array)           Optional child rows — makes the row expandable/collapsible.
 *   selectable (boolean)    When false, the row's multi-select checkbox is disabled/locked.
 *
 * ─────────────────────────────────────────────
 * EXAMPLES
 * ─────────────────────────────────────────────
 *
 * Simple column:
 *   { key: "name", label: "Name" }
 *
 * Fixed column pinned to the left:
 *   { key: "id", label: "ID", isFixed: true, style: { width: "80px" } }
 *
 * Right-pinned actions column:
 *   { key: "actions", label: "", isFixed: true, fixDirection: "right", style: { width: "50px" },
 *     render: (_, row) => <Button onClick={() => edit(row)}>Edit</Button> }
 *
 * Currency column with auto-totalled footer:
 *   { key: "totalPrice", label: "Total Price", header: <>Total Sell</>,
 *     dataType: "currency", canBeTotalled: true, showInFooter: true,
 *     align: "right", className: "p-1", style: { minWidth: "165px" } }
 *
 * Percentage column with custom footer calculation:
 *   { key: "margin", label: "Margin %", dataType: "percentage", align: "right",
 *     calcFooter: (footer) => footer.profit / footer.revenue * 100,
 *     render: (value) => <span>{value}%</span> }
 *
 * Grouped columns:
 *   { key: "groupName", label: "Group", groupHeader: true }
 *   { key: "item1", label: "Item 1", groupColumn: true, group: "Items" }
 *   { key: "item2", label: "Item 2", groupColumn: true, group: "Items" }
 */
const MIN_COL_WIDTH = 60;

const getColWidth = (col) => {
    const raw = col.style?.width || col.style?.minWidth || 150;
    const px = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    return Math.max(px, MIN_COL_WIDTH);
};

// Standalone component so indeterminate can be managed via a stable ref+useEffect,
// independent of the parent component's render cycle and Reactstrap's innerRef handling.
const SelectAllCheckbox = ({ checked, indeterminate, onChange, id }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <input
            type="checkbox"
            id={id}
            ref={ref}
            className={`form-check-input ${indeterminate ? 'border-warning' : ''}`}
            style={indeterminate ? {
                backgroundColor: 'var(--bs-warning)',
                borderColor: 'var(--bs-warning)'
            } : {}}
            checked={checked}
            onChange={onChange}
        />
    );
};

const DataTableWindowed = ({
    id,
    columns,
    data,
    isLoading,
    isUpdating,
    error,
    searchableFields = [],
    paging,
    onRowClick,
    filters = [],
    actions = [],
    exportToCsv = false,
    csvFileName = "data.csv",
    sortByColumn = null,
    sortDirection = "ascending",
    footer = null,
    showAutogeneratedFooter = false,
    onFilterChange,
    allowColumnCustomisation = false,
    columnProfiles,
    currentProfileKey = "All",
    height = null,
    fillContainerHeight = false,
    shrinkHeightToContents = false,
    showGroupHeaders = true,
    allowMultiSelect = false,
    onSelectedDataChange,
    onFilteredDataChange
}) => {
    const theme = useTheme();
    const generatedId = useId();
    const instancePrefix = id ?? generatedId;

    const [customFilteredData, setCustomFilteredData] = useState([]);

    // Helper to collect all leaf descendant IDs (items with no children)
    const getLeafDescendantIds = (item) => {
        const ids = [];
        if (item.items && Array.isArray(item.items) && item.items.length > 0) {
            // Has children, recurse into them
            item.items.forEach(child => {
                ids.push(...getLeafDescendantIds(child));
            });
        } else {
            // No children, this is a leaf node
            ids.push(item.id);
        }
        return ids;
    };
    
    const getLeafItems = (items) => {
        let leafItems = [];
        items.forEach(item => {
            if (item.items && Array.isArray(item.items) && item.items.length > 0) {
                leafItems.push(...getLeafItems(item.items));
            } else {
                leafItems.push(item);
            }
        });
        return leafItems;
    }

    const leafItems = useMemo(() => {
        return getLeafItems(customFilteredData);
    }, [customFilteredData]);

    const getCheckboxState = (root) => {
        const hasChildren = root.items && Array.isArray(root.items) && root.items.length > 0;
        const leafIds = hasChildren ? getLeafDescendantIds(root) : [root.id];
        const selectedLeafCount = leafIds.filter(id => selectedRowIds.has(id)).length;
        
        const allLeafSelected = selectedLeafCount === leafIds.length && leafIds.length > 0;
        const someLeafSelected = selectedLeafCount > 0 && selectedLeafCount < leafIds.length;
        
        const isChecked = allLeafSelected;
        const isIndeterminate = someLeafSelected;

        return { isChecked, isIndeterminate };
    }

    // NEW: selection state (by id) + current filtered ids from DataTable
    const [selectedRowIds, setSelectedRowIds] = useState(new Set());
    const [filteredRowIds, setFilteredRowIds] = useState(new Set(getLeafDescendantIds({items: customFilteredData})));

    const getDefaultColumnProfiles = useCallback(() => {
        return {
            [currentProfileKey]: columns.map(col => ({
                key: col.key,
                isDefault: col.isDefault,
                isRequired: col.isSystem,
            }))
        }
    }, [columns, currentProfileKey]);

    // how many of the currently *filtered* rows are selected?
    const selectedInFilteredCount = useMemo(() => {
        let c = 0;
        filteredRowIds.forEach(id => { if (selectedRowIds.has(id)) c++; });
        return c;
    }, [filteredRowIds, selectedRowIds]);

    const headerChecked = filteredRowIds.size > 0 && selectedInFilteredCount === filteredRowIds.size;
    const headerIndeterminate = selectedInFilteredCount > 0 && selectedInFilteredCount < filteredRowIds.size;
    const headerDisabled = filteredRowIds.size === 0;

    const headerSelectRef = useRef(null);
    useEffect(() => {
        if (headerSelectRef.current) {
            headerSelectRef.current.indeterminate = headerIndeterminate;
        }
    }, [headerIndeterminate]);

    // Select-all toggle state
    const allFilteredSelected = useMemo(
        () => filteredRowIds.size > 0 && [...filteredRowIds].every(id => selectedRowIds.has(id)),
        [filteredRowIds, selectedRowIds]
    );

    const toggleSelectFiltered = useCallback(() => {
        setSelectedRowIds(prev => {
            const next = new Set(prev);
            if (allFilteredSelected) {
                filteredRowIds.forEach(id => next.delete(id));
            } else {
                filteredRowIds.forEach(id => next.add(id));
            }
            return next;
        });
    }, [allFilteredSelected, filteredRowIds]);

    // Reset selections when the dataset is rebuilt (e.g., new upload)
    useEffect(() => {
        setFilteredRowIds(new Set(getLeafDescendantIds({ items: customFilteredData })));
    }, [customFilteredData]);

    const selectCol = useMemo(() => ({
        key: "select",
        label: "select",
        isFixed: true,
        style: { width: "50px", minWidth: "50px", padding: "0", textAlign: "center" },
        group: "",
        header: (() => {

            const isChecked = selectedInFilteredCount > 0 && selectedInFilteredCount === filteredRowIds.size;
            const isIndeterminate = selectedInFilteredCount > 0 && selectedInFilteredCount < filteredRowIds.size; 
            
            return (
                <Input
                    innerRef={headerSelectRef}
                    type="checkbox"
                    ref={(el) => {
                        if (el) {
                            el.indeterminate = isIndeterminate;
                        }
                    }}
                    className={`form-check-input ${isIndeterminate ? 'border-warning' : ''}`}
                    style={isIndeterminate ? { 
                        backgroundColor: 'var(--bs-warning)',
                        borderColor: 'var(--bs-warning)'
                    } : {}}
                    checked={isChecked}
                    disabled={headerDisabled}
                    onChange={toggleSelectFiltered}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Toggle select all (filtered)"
                />
            )
        })(),
        isSortDisabled: true,
        render: (_, row) => {
            if (row.selectable === false) {
                return (
                    <span title="This item cannot be selected" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: 0.5, cursor: 'not-allowed' }}>
                        <input
                            type="checkbox"
                            disabled
                            className="form-check-input"
                            style={{ pointerEvents: 'none', marginRight: 2 }}
                        />
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Locked" style={{ flexShrink: 0 }}>
                            <path d="M4.5 7V5.5C4.5 3.57 6.07 2 8 2C9.93 2 11.5 3.57 11.5 5.5V7" stroke="#888" strokeWidth="1.2"/>
                            <rect x="3" y="7" width="10" height="7" rx="2" fill="#eee" stroke="#888" strokeWidth="1.2"/>
                            <circle cx="8" cy="10.5" r="1" fill="#bbb" />
                        </svg>
                    </span>
                );
            }

            const { isIndeterminate, isChecked } = getCheckboxState(row)

            return (
                <input
                    ref={(el) => {
                        if (el) {
                            el.indeterminate = isIndeterminate;
                        }
                    }}
                    type="checkbox"
                    className={`form-check-input ${isIndeterminate ? 'border-warning' : ''}`}
                    style={isIndeterminate ? { 
                        backgroundColor: 'var(--bs-warning)',
                        borderColor: 'var(--bs-warning)'
                    } : {}}
                    checked={isChecked}
                    onChange={(e) => {
                        e.stopPropagation();
                        const checked = e.target.checked;
                        setSelectedRowIds(prev => {
                            const next = new Set(prev);

                            const setItemAndChildren = (item) => {
                                if (checked) {
                                    if (item.items && Array.isArray(item.items)) {
                                        item.items.forEach(childItem => setItemAndChildren(childItem));
                                    } else {
                                        next.add(item.id);
                                    }
                                }
                                else {
                                    if (item.items && Array.isArray(item.items)) {
                                        item.items.forEach(childItem => setItemAndChildren(childItem));
                                    } else {
                                        next.delete(item.id);
                                    }
                                }
                            }

                            setItemAndChildren(row);

                            return next;
                        });
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        },
    }), [headerChecked, headerDisabled, toggleSelectFiltered, selectedRowIds]);

    const [profiles, setProfiles] = useState(() => {
        if (columnProfiles) return columnProfiles;
        
        // Generate default profile from columns
        return {
            [currentProfileKey]: columns.map(col => ({
                key: col.key,
                isDefault: col.isDefault,
                isRequired: col.isSystem,
            }))
        };
    });
    const [currentColumnProfile, setCurrentColumnProfile] = useState(profiles[currentProfileKey] || []);

    const getDefaultVisibilityForProfile = () => {

        if (currentColumnProfile) {
            return currentColumnProfile.reduce((acc, columnInProfile) => {
                acc[columnInProfile.key] = columnInProfile.isDefault || columnInProfile.isRequired;
                return acc;
            }, {});
        }

        // Column profiles not found, so default to all columns visible
        return columns.reduce((acc, columnInProfile) => {
            acc[columnInProfile.key] = true;
            return acc;
        }, {});
    };

    const getVisibilityForProfileFromLocalStore = (location) => {
        const stored = localStorage.getItem(location);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);

                // Ensure required columns are true after reading from local storage, just in case
                // the defaults have changed since the profile was saved.

                // Also strip any keys that are no longer in the current profile — e.g. a column
                // that was visible under a previous profile/tab but isn't allowed in this one.
                const profileKeys = new Set(currentColumnProfile.map(x => x.key));
                const withRequiredTrue = Object.fromEntries(
                    Object.entries(parsed).filter(([key]) => profileKeys.has(key))
                );
                currentColumnProfile.forEach(({ key, isRequired }) => {
                    if (isRequired) {
                        withRequiredTrue[key] = true;
                    }
                });

                return withRequiredTrue;
            } catch {
                return getDefaultVisibilityForProfile(currentProfileKey);
            }
        }
        return getDefaultVisibilityForProfile(currentProfileKey);
    };

    const COLUMN_SETTINGS_KEY = `${id}_ColumnSettings_${currentProfileKey}`;
    const FIXED_COLUMNS_KEY = `${id}_FixedColumns_${currentProfileKey}`;

    const [visibleColumnsAsObject, setVisibleColumnsAsObject] = useState(() => {
        return getVisibilityForProfileFromLocalStore(COLUMN_SETTINGS_KEY);
    });

    const [fixedColumnsAsObject, setFixedColumnsAsObject] = useState(() => {
        const stored = localStorage.getItem(FIXED_COLUMNS_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed !== null && typeof parsed === 'object') {
                    // Merge stored prefs with column definitions so new columns
                    // that aren't in storage yet get their isFixed default applied.
                    const merged = { ...parsed };
                    columns.forEach(col => {
                        if (!(col.key in merged) && col.isFixed) {
                            merged[col.key] = true;
                        }
                    });
                    return merged;
                }
            } catch (e) {
                // fall through to derive from columns
            }
        }
        // Initialize with columns that have isFixed property set
        return columns.reduce((acc, col) => {
            if (col.isFixed) {
                acc[col.key] = true;
            }
            return acc;
        }, {});
    });

    useEffect(() => {
        setCurrentColumnProfile(profiles[currentProfileKey] || []);
    }, [currentProfileKey, profiles]);

    // Keep the internal profiles state in sync with the columnProfiles prop.
    // Without this, new tab profiles added after mount (e.g. a new item type) are
    // never reflected in profiles, so switching to that tab yields an empty table.
    useEffect(() => {
        if (columnProfiles) setProfiles(columnProfiles);
    }, [columnProfiles]);

    // Update visible columns when columns prop changes
    useEffect(() => {
        if (!columnProfiles) {
            const newProfile = getDefaultColumnProfiles()[currentProfileKey] || [];
            setProfiles(prev => ({ ...prev, [currentProfileKey]: newProfile }));

            // Try localStorage first — respect any user-saved preferences.
            // Use newProfile directly (not stale currentColumnProfile) to enforce required columns.
            const stored = localStorage.getItem(COLUMN_SETTINGS_KEY);
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    if (parsed !== null && typeof parsed === 'object') {
                        const profileKeys = new Set(newProfile.map(x => x.key));
                        const withRequired = Object.fromEntries(
                            Object.entries(parsed).filter(([key]) => profileKeys.has(key))
                        );
                        // Apply defaults for new columns not yet in storage
                        newProfile.forEach(({ key, isRequired, isDefault }) => {
                            if (isRequired) withRequired[key] = true;
                            else if (!(key in withRequired) && isDefault) withRequired[key] = true;
                        });
                        setVisibleColumnsAsObject(withRequired);
                        return;
                    }
                } catch (e) {
                    // fall through to profile defaults
                }
            }
            // No stored preference — derive visibility from the fresh profile defaults
            const newVisibility = newProfile.reduce((acc, col) => {
                acc[col.key] = !!(col.isDefault || col.isRequired);
                return acc;
            }, {});
            setVisibleColumnsAsObject(newVisibility);
        } else {
            // Read the active profile directly from the prop so we always get the
            // up-to-date entry, even if currentColumnProfile state is still stale
            // (e.g. when switching to a tab whose profile was just added).
            const freshProfile = columnProfiles[currentProfileKey] ?? [];
            const stored = localStorage.getItem(COLUMN_SETTINGS_KEY);
            if (stored) {
                try {
                    const parsed = JSON.parse(stored);
                    if (parsed !== null && typeof parsed === 'object') {
                        const profileKeys = new Set(freshProfile.map(x => x.key));
                        const withRequired = Object.fromEntries(
                            Object.entries(parsed).filter(([key]) => profileKeys.has(key))
                        );
                        // Apply defaults for new columns not yet in storage
                        freshProfile.forEach(({ key, isRequired, isDefault }) => {
                            if (isRequired) withRequired[key] = true;
                            else if (!(key in withRequired) && isDefault) withRequired[key] = true;
                        });
                        setVisibleColumnsAsObject(withRequired);
                        return;
                    }
                } catch (e) {
                    // fall through to profile defaults
                }
            }
            // No stored preference — derive from the fresh profile defaults
            const defaultVisibility = freshProfile.reduce((acc, col) => {
                acc[col.key] = !!(col.isDefault || col.isRequired);
                return acc;
            }, {});
            setVisibleColumnsAsObject(defaultVisibility);
        }
    }, [columns, COLUMN_SETTINGS_KEY, columnProfiles, currentProfileKey, getDefaultColumnProfiles]);

    // Update fixed columns when columns prop changes (pick up isFixed from newly arrived columns)
    useEffect(() => {
        const stored = localStorage.getItem(FIXED_COLUMNS_KEY);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (parsed !== null && typeof parsed === 'object') {
                    // Merge stored prefs with column definitions so new columns
                    // that aren't in storage yet get their isFixed default applied.
                    const merged = { ...parsed };
                    columns.forEach(col => {
                        if (!(col.key in merged) && col.isFixed) {
                            merged[col.key] = true;
                        }
                    });
                    setFixedColumnsAsObject(merged);
                    return;
                }
                // parsed was null or not an object — fall through to derive from columns
            } catch (e) {
                // fall through to derive from columns
            }
        }
        // No stored preference — derive from columns' isFixed property
        setFixedColumnsAsObject(
            columns.reduce((acc, col) => {
                if (col.isFixed) acc[col.key] = true;
                return acc;
            }, {})
        );
    }, [columns, FIXED_COLUMNS_KEY]);

    const visibleColumns = useMemo(() => {
        const fixedLookup = fixedColumnsAsObject ?? {};
        const visibleLookup = visibleColumnsAsObject ?? {};
        const cols = columns
            .filter(col => visibleLookup[col.key])
            .map(col => ({
                ...col,
                isFixed: fixedLookup[col.key] || false
            }));
        
        // Reorder columns: left-fixed first, then scrollable, then right-fixed
        const leftFixed = cols.filter(col => col.isFixed && col.fixDirection !== "right");
        const scrollable = cols.filter(col => !col.isFixed);
        const rightFixed = cols.filter(col => col.isFixed && col.fixDirection === "right");
        
        return [...(allowMultiSelect ? [selectCol] : []), ...leftFixed, ...scrollable, ...rightFixed];
    }, [columns, visibleColumnsAsObject, fixedColumnsAsObject, selectCol, allowMultiSelect]);

    // Calculate fixed column positions and identify left/right fixed columns
    const fixedColumnsInfo = useMemo(() => {
        const leftFixedPositions = new Map();
        const rightFixedPositions = new Map();
        const rightFixedKeys = new Set();
        const scrollable = [];
        let leftCumulativeWidth = 0;
        let leftFixedEdgeKey = null;
        let rightFixedEdgeKey = null;

        // Collect right-fixed columns in order for offset calculation
        const rightFixedCols = [];

        // Since columns are already reordered, process them sequentially
        visibleColumns.forEach((col) => {
            if (col.isFixed && col.fixDirection === "right") {
                // Right-fixed column
                rightFixedKeys.add(col.key);
                rightFixedCols.push(col);
                if (!rightFixedEdgeKey) {
                    rightFixedEdgeKey = col.key; // First right-fixed is the edge
                }
            } else if (col.isFixed) {
                // Left-fixed column
                leftFixedPositions.set(col.key, leftCumulativeWidth);
                leftCumulativeWidth += getColWidth(col);
                leftFixedEdgeKey = col.key; // Last left-fixed is the edge
            } else {
                // Scrollable column
                scrollable.push(col);
            }
        });

        // Build right-fixed offsets from the right edge inward.
        // The rightmost column gets right: 0, the next gets right: <width of rightmost>, etc.
        let rightCumulativeWidth = 0;
        for (let i = rightFixedCols.length - 1; i >= 0; i--) {
            rightFixedPositions.set(rightFixedCols[i].key, rightCumulativeWidth);
            rightCumulativeWidth += getColWidth(rightFixedCols[i]);
        }

        const rightFixedTotalWidth = rightCumulativeWidth;

        // Compute total table width so we can set an explicit width on the <table>,
        // preventing the browser's auto table-layout from redistributing column widths.
        const totalTableWidth = visibleColumns
            .filter(col => !col.groupHeader)
            .reduce((sum, col) => sum + getColWidth(col), 0);

        return {
            leftFixedPositions,
            rightFixedPositions,
            rightFixedKeys,
            scrollableColumns: scrollable,
            leftFixedWidth: leftCumulativeWidth,
            rightFixedWidth: rightFixedTotalWidth,
            leftFixedEdgeKey,
            rightFixedEdgeKey,
            totalTableWidth
        };
    }, [visibleColumns]);

    const { leftFixedPositions, rightFixedPositions, rightFixedKeys, scrollableColumns, leftFixedWidth, rightFixedWidth, leftFixedEdgeKey, rightFixedEdgeKey, totalTableWidth } = fixedColumnsInfo;



    // Helper function to get solid background color for headers and footers (non-striped)
    const getSolidHeaderFooterColor = useCallback(() => {
        // Use the same color as the second row in table striping (odd row = contrast1)
        return "var(--bg-color-contrast1)";
    }, []);

    // Helper function to apply solid header/footer background color
    const applySolidHeaderFooterColor = useCallback((baseColor) => {
        // Return solid header/footer color for consistency
        return getSolidHeaderFooterColor();
    }, [getSolidHeaderFooterColor]);

    // Helper function to get fixed style for a column
    const getFixedStyle = useCallback((col, rowIndex) => {
        if (!col.isFixed) return {};

        // For headers/footers (rowIndex < 0 or undefined), use solid color
        // For body rows, use striped colors matching non-fixed columns
        const bgColor = rowIndex >= 0
            ? (rowIndex % 2 === 0 ? "var(--bg-color-contrast2)" : "var(--bg-color-contrast1)")
            : getSolidHeaderFooterColor();

        // Choose a light, "white-ish" border color for the fixed edge
        const edgeBorderColor = theme === "dark-mode"
            ? "rgba(255, 255, 255, 0.85)"
            : "rgba(255, 255, 255, 0.9)";

        // Lock the width of sticky columns so the browser's auto table-layout
        // algorithm cannot shrink them during native horizontal scroll.
        // Use getColWidth as fallback to match the <colgroup> width (default 150px, min 60px).
        const computedWidth = `${getColWidth(col)}px`;
        const stickySize = { width: computedWidth, minWidth: computedWidth };

        // Check if this is a left-fixed column
        if (leftFixedPositions.has(col.key)) {
            const isEdge = col.key === leftFixedEdgeKey && scrollableColumns.length > 0;
            const shadowColor = "3px 0 0 rgba(0, 0, 0, .18), 10px 0 10px -4px rgba(0, 0, 0, 0.22), 18px 0 18px -10px rgba(0, 0, 0, 0.12)"
            return {
                position: "sticky",
                left: leftFixedPositions.get(col.key),
                zIndex: 10,
                background: bgColor,
                ...stickySize,
                borderRight: isEdge ? `1px solid ${edgeBorderColor}` : undefined,
                boxShadow: isEdge ? shadowColor : undefined
            };
        }

        // Check if this is a right-fixed column
        if (rightFixedKeys.has(col.key)) {
            const isEdge = col.key === rightFixedEdgeKey && scrollableColumns.length > 0;
            const shadowColor = "-3px 0 0 rgba(0, 0, 0, .18), -10px 0 10px -4px rgba(0, 0, 0, 0.22), -18px 0 18px -10px rgba(0, 0, 0, 0.12)";

            return {
                position: "sticky",
                right: rightFixedPositions.get(col.key) || 0,
                zIndex: 11,
                background: bgColor,
                ...stickySize,
                borderLeft: isEdge ? `1px solid ${edgeBorderColor}` : undefined,
                boxShadow: isEdge ? shadowColor : undefined
            };
        }

        return {};
    }, [leftFixedPositions, rightFixedPositions, rightFixedKeys, theme, leftFixedEdgeKey, rightFixedEdgeKey, getSolidHeaderFooterColor, scrollableColumns.length]);

    const SEARCH_FIELDS_SETTINGS_KEY = !id ? undefined : currentProfileKey ? `searchFieldsSettings_${id}_${currentProfileKey}` : `searchFieldsSettings_${id}`

    // Advanced filters state and animations
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
    const mountedStyle = { animation: "inAnimation 250ms ease-in" };
    const unmountedStyle = { animation: "outAnimation 270ms ease-out", animationFillMode: "forwards" };

    // configuration for the settings modal
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const toggleSettingsModal = () => {
        setCurrentActiveColumnSettingsGroupTab(columnGroups[0]);
        setSettingsModalOpen(!settingsModalOpen)
    };

    // Fullscreen mode state
    const [isFullscreen, setIsFullscreen] = useState(false);
    const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

    const [currentActiveColumnSettingsGroupTab, setCurrentActiveColumnSettingsGroupTab] = useState(columns.map(col => col.group).find(group => group) || "Ungrouped"    );

    useEffect(() => {
        if (!profiles) {
            // If column profiles aren't provided, then we'll work it out from supplied columns
            setProfiles({
                All: columns.map(col => ({
                    key: col.key,
                    isDefault: (col.isSystem ?? false) || (col.isDefault ?? false), 
                    isRequired: col.isSystem ?? false,
                }))
            })
        }

    }, [columns, columnProfiles]);


    const defaultVisibleColumns = visibleColumns.reduce((acc, col) => {
        acc[col.key] = true;
        return acc;
    }, {});

    const prevInUseSearchKeysRef = useRef([]);
    const inUseSearchKeys = useMemo(() => {
        // We only want to be able to search inside columns that are actually being shown
        const keys = searchableFields && 
               Array.isArray(searchableFields) &&
               searchableFields.length > 0 
                ? searchableFields
                    .filter(searchKey => visibleColumns.some(col => col.key === searchKey && col.hideFromSettingsDialog !== true))
                    .sort((a, b) => a.localeCompare(b)) 
                    : [];
        // Return the previous reference if the content hasn't changed,
        // so downstream memos (activeSearchKeys, etc.) stay stable.
        const prev = prevInUseSearchKeysRef.current;
        if (keys.length === prev.length && keys.every((k, i) => k === prev[i])) {
            return prev;
        }
        prevInUseSearchKeysRef.current = keys;
        return keys;
    }, [searchableFields, visibleColumns]);

    const allInUseSearchKeys = useMemo(() => {
        return inUseSearchKeys.reduce((obj, key) => {
            obj[key] = true;
            return obj;
        }, {})
    }, [inUseSearchKeys]);

    const [selectedSearchKeys, setSelectedSearchKeys] = useState(() =>
        // Default to all searchable fields selected
        allInUseSearchKeys
    );

    const listOfFieldsBeingSearched = Object.keys(selectedSearchKeys)
                                        .filter(key => selectedSearchKeys[key] && inUseSearchKeys.includes(key))
                                        .map(key => visibleColumns.find(x => x.key === key)?.label || key )
                                        .join(", ")

    // Build a stable map of key → sortValue fn, mirroring searchValueFns map.
    const prevSortValueFnsRef = useRef({});
    const sortValueFns = useMemo(() => {
        const fns = {};
        columns.forEach(col => {
            if (typeof col.sortValue === 'function') {
                fns[col.key] = col.sortValue;
            } else if (typeof col.searchValue === 'function') {
                // If no explicit sortValue fn is provided, but there's a searchValue fn, use that for sorting too.
                fns[col.key] = col.searchValue;
            }
        });
        const prev = prevSortValueFnsRef.current;
        const keys = Object.keys(fns);
        const prevKeys = Object.keys(prev);
        if (keys.length === prevKeys.length && keys.every(k => fns[k] === prev[k])) {
            return prev;
        }
        prevSortValueFnsRef.current = fns;
        return fns;
    }, [columns]);

    // Search and sorting hooks
    const { sortedData, sortBy, sortConfig } = useSortData(data, { key: sortByColumn ? sortByColumn : visibleColumns[0]?.key, direction: sortDirection }, sortValueFns);
    const activeSearchKeys = useMemo(
        () => Object.keys(selectedSearchKeys).filter(key => selectedSearchKeys[key] && inUseSearchKeys.includes(key)),
        [selectedSearchKeys, inUseSearchKeys]
    );

    // Build a map of key → searchValue fn for columns that declare one.
    // Built from `columns` (the stable prop) rather than `visibleColumns`, because
    // visibleColumns includes selectCol which changes on every selection state change,
    // which would make searchValueFns unstable and trigger an infinite render loop.
    // activeSearchKeys already limits which keys are actually searched, so including
    // non-visible columns here is harmless.
    //
    // We return the previous reference if the actual function references haven't changed
    // (same pattern as inUseSearchKeys), so that an unstable `columns` array from the
    // caller doesn't cause filteredData to recompute on every render.
    const prevSearchValueFnsRef = useRef({});
    const searchValueFns = useMemo(() => {
        const fns = {};
        columns.forEach(col => {
            if (typeof col.searchValue === 'function') {
                fns[col.key] = col.searchValue;
            }
        });
        const prev = prevSearchValueFnsRef.current;
        const keys = Object.keys(fns);
        const prevKeys = Object.keys(prev);
        if (keys.length === prevKeys.length && keys.every(k => fns[k] === prev[k])) {
            return prev;
        }
        prevSearchValueFnsRef.current = fns;
        return fns;
    }, [columns]);

    const [searchFilteredData, setSearch] = useSearchData(sortedData, activeSearchKeys, searchValueFns);
    const [filteredData, setFilter] = useFilterDataByField(searchFilteredData);

    const [filterValues, setFilterValues] = useState(
        filters.map((x) => ({ id: x.id, value: x.defaultValue, customIncludeFunction: x.customIncludeFunction }))
    );

    useLayoutEffect(() => {
        const customFilters = filterValues.filter((x) => typeof x.customIncludeFunction === "function");
        // Fast path: when there are no custom filter functions, pass
        // the data through unchanged — avoids an O(n) .filter() call
        // inside this synchronous (paint-blocking) layout effect.
        if (customFilters.length === 0) {
            setCustomFilteredData(filteredData);
        } else {
            setCustomFilteredData(filteredData.filter((dataItem) =>
                customFilters.every((filter) => filter.customIncludeFunction(dataItem, filter.value))
            ));
        }
    }, [filteredData, filterValues]);

    const prevSelectedCallbackIdsRef = useRef(new Set());
    const prevDataRef = useRef(data);
    useEffect(() => {
        if (!onSelectedDataChange) return;

        const getChildRows = (items) => {
            let result = [];
            for (const item of items) {
                if (item.items && Array.isArray(item.items) && item.items.length > 0) {
                    result = result.concat(getChildRows(item.items));
                } else {
                    result.push(item);
                }
            }
            return result;
        }

        const flattenedFilteredData = getChildRows(customFilteredData);
        const selectedData = flattenedFilteredData.filter(row => selectedRowIds.has(row.id));

        // Only notify the parent when the set of selected IDs actually
        // changes — OR when the underlying data prop changed (same IDs but
        // updated row content, e.g. after a bulk save).  Without the ID
        // guard a new customFilteredData reference with identical content
        // would trigger the callback, the parent would setState, re-render
        // this component, and create an infinite loop.
        const currentIds = new Set(selectedData.map(r => r.id));
        const prev = prevSelectedCallbackIdsRef.current;
        const dataChanged = prevDataRef.current !== data;
        prevDataRef.current = data;

        if (!dataChanged && currentIds.size === prev.size && [...currentIds].every(id => prev.has(id))) {
            return; // nothing changed — skip callback
        }
        prevSelectedCallbackIdsRef.current = currentIds;
        onSelectedDataChange(selectedData);
    }, [selectedRowIds, customFilteredData, onSelectedDataChange, data]);

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const toggle = () => setDropdownOpen((prevState) => !prevState);

    const allSearchFieldsChecked = inUseSearchKeys.length > 0 && inUseSearchKeys.every(key => selectedSearchKeys[key]);
    const someSearchFieldsChecked = inUseSearchKeys.some(key => selectedSearchKeys[key]);
    const searchFieldsIndeterminate = !allSearchFieldsChecked && someSearchFieldsChecked;

    const handleSelectAllSearchFieldsChange = () => {
        const newKeys = allSearchFieldsChecked
            ? Object.fromEntries(inUseSearchKeys.map(k => [k, false]))
            : { ...allInUseSearchKeys };
        setSelectedSearchKeys(newKeys);
        if (SEARCH_FIELDS_SETTINGS_KEY) {
            localStorage.setItem(SEARCH_FIELDS_SETTINGS_KEY, JSON.stringify(newKeys));
        }
    };

    const handleCheckboxChange = (itemKey) => {
        setSelectedSearchKeys(prev => {
            const newState = {
                ...prev,
                [itemKey]: !prev[itemKey]
            }

            localStorage.setItem(SEARCH_FIELDS_SETTINGS_KEY, JSON.stringify(newState));

            return newState
        });
    };

    useEffect(() => {
        // If the id of the table changes, we presumably are in a different view, so we must load
        // new settings for the searchable fields

        if (SEARCH_FIELDS_SETTINGS_KEY) {
            const stored = localStorage.getItem(SEARCH_FIELDS_SETTINGS_KEY);
            
            if (stored) {
                try {
                    setSelectedSearchKeys(JSON.parse(stored));
                } catch {
                    setSelectedSearchKeys(allInUseSearchKeys);
                }
            } else {
                setSelectedSearchKeys(allInUseSearchKeys);
            }
        }
    }, [SEARCH_FIELDS_SETTINGS_KEY]);

    // locking state
    const lockState = useLockState();

    useEffect(() => {
        // If the user alters what columns are shown, we will default any new ones to being shown in the search
        setSelectedSearchKeys(prevSelectedItems => {
            const updatedSelectedItems = { ...prevSelectedItems };
            inUseSearchKeys.forEach(key => {
                if (!(key in updatedSelectedItems)) {
                    updatedSelectedItems[key] = true;
                }
            });
            return updatedSelectedItems;
        });
    }, [inUseSearchKeys]);

    const countChildItems = (item) => {
        let count = 0;

        if (item.items && Array.isArray(item.items) && item.items.length > 0) {
            for (const child of item.items) {
                count += countChildItems(child);
            }
        } else {
            count += 1;
        }

        return count;
    }

    const numRecords = useMemo(() => {
        let count = 0;

        for (const item of data) {
            count += countChildItems(item);
        }

        return count;
    }
    , [data]);

    const numFilteredRecords = useMemo(() => {
        let count = 0;

        for (const item of customFilteredData) {
            count += countChildItems(item);
        }

        return count;
    }
    , [customFilteredData]);

    // Nested row state
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [expandCollapseButtonState, setExpandCollapseButtonState] = useState("expand all");

    // Refs
    const tableRef = React.useRef(null);
    const tableContainerRef = React.useRef(null);
    const tbodyRef = React.useRef(null);
    const componentRootRef = useRef(null);
    const [filledContainerHeightPx, setFilledContainerHeightPx] = useState(null);
    const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = useState(false);
    const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState(false);

    useLayoutEffect(() => {
        if (!fillContainerHeight || isFullscreen) return;

        const tableContainer = tableContainerRef.current;
        const tableContainerHost = tableContainer?.parentElement;
        if (!tableContainer || !tableContainerHost) return;

        const updateHeight = () => {
            const hostHeight = Math.floor(tableContainerHost.clientHeight || 0);
            if (hostHeight <= 0) return;

            const marginTop = Math.ceil(parseFloat(window.getComputedStyle(tableContainer).marginTop) || 0);
            const next = Math.max(240, hostHeight - marginTop);

            setFilledContainerHeightPx((prev) => {
                if (prev != null && Math.abs(next - prev) <= 1) return prev;
                return next;
            });
        };

        updateHeight();

        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(() => updateHeight());
        observer.observe(tableContainerHost);
        observer.observe(tableContainer);
        
        // Trigger updates as layout settles
        const timeoutId = setTimeout(updateHeight, 100);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, [fillContainerHeight, isFullscreen]);

    const requestedHeight = useMemo(() => {
        if (!(fillContainerHeight && !isFullscreen)) return height;
        if (filledContainerHeightPx != null) return `${filledContainerHeightPx}px`;

        const parsed = parseInt(height, 10);
        if (Number.isFinite(parsed) && parsed > 0) return `${parsed}px`;

        return "600px";
    }, [fillContainerHeight, isFullscreen, filledContainerHeightPx, height]);
    
    // Scroll offset stored as a ref so scroll events that don't change the
    // visible range never trigger a re-render (breaks the scroll → state →
    // render → layout-shift → scroll feedback loop that causes vibration).
    const scrollOffsetRef = useRef(0);
    
    // Flatten data structure based on expansion state
    const allVisibleData = useMemo(() => {
        const globalSeenKeys = new Set();
        const flatten = (items, level = 0, parentKey = '') => {
            let result = [];
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                const itemId = item.id != null ? item.id : `idx-${i}`;
                let rowKey = `${parentKey}-${itemId}`;
                
                // Ensure unique keys — if two siblings share the same id,
                // append the sibling index to disambiguate.
                if (globalSeenKeys.has(rowKey)) {
                    rowKey = `${parentKey}-${itemId}_${i}`;
                }
                globalSeenKeys.add(rowKey);
                
                const hasChildren = Array.isArray(item.items) && item.items.length > 0;

                result.push({ 
                    ...item,
                    __level: level, 
                    __rowKey: rowKey, 
                    __hasChildren: hasChildren,
                    __parentKey: parentKey
                });
                
                // Flatten children if this row is expanded
                if (hasChildren && expandedRows.has(rowKey)) {
                    result = result.concat(flatten(item.items, level + 1, rowKey));
                }
            }
            return result;
        };
        
        return flatten(customFilteredData);
    }, [customFilteredData, expandedRows]);

    // When shrinkHeightToContents is enabled, disable the fixed-height scroll
    // container (and therefore virtual scrolling) if all visible rows fit within
    // the specified height.  Uses the same 45px row-height assumption used by
    // the virtual scrolling calculations elsewhere.
    const ROW_HEIGHT = 45;
    const effectiveHeight = useMemo(() => {
        if (!requestedHeight) return null;
        if (!shrinkHeightToContents) return requestedHeight;
        const contentHeight = allVisibleData.length * ROW_HEIGHT;
        return contentHeight < parseInt(requestedHeight, 10) ? null : requestedHeight;
    }, [requestedHeight, shrinkHeightToContents, allVisibleData.length]);

    // Keep a ref to the latest data length so the scroll handler (which
    // doesn't re-bind on every data change) can always read the current value.
    const allVisibleDataLengthRef = useRef(allVisibleData.length);
    allVisibleDataLengthRef.current = allVisibleData.length;

    useLayoutEffect(() => {
        const tableContainer = tableContainerRef.current;
        const table = tableRef.current;
        if (!tableContainer) return;

        const updateScrollbarPresence = () => {
            const nextHasHorizontalScrollbar = tableContainer.scrollWidth > tableContainer.clientWidth + 1;
            const nextHasVerticalScrollbar = tableContainer.scrollHeight > tableContainer.clientHeight + 1;
            setHasHorizontalScrollbar((prev) => (prev === nextHasHorizontalScrollbar ? prev : nextHasHorizontalScrollbar));
            setHasVerticalScrollbar((prev) => (prev === nextHasVerticalScrollbar ? prev : nextHasVerticalScrollbar));
        };

        updateScrollbarPresence();

        if (typeof ResizeObserver === "undefined") return;

        const observer = new ResizeObserver(() => updateScrollbarPresence());
        observer.observe(tableContainer);
        if (table) observer.observe(table);

        return () => {
            observer.disconnect();
        };
    }, [visibleColumns, allVisibleData.length, effectiveHeight, isFullscreen, fillContainerHeight]);
    
    // Visible range is state, only updated when start/end actually change.
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });

    // Recompute visible range when data length or effectiveHeight changes
    useEffect(() => {
        if (!effectiveHeight) {
            const next = { start: 0, end: allVisibleData.length };
            prevRangeRef.current = next;
            setVisibleRange(prev =>
                prev.start === 0 && prev.end === allVisibleData.length
                    ? prev
                    : next
            );
            return;
        }
        const containerHeight = parseInt(effectiveHeight);
        // The browser silently resets scrollTop when content shrinks (e.g. search applied
        // then cleared) without firing a scroll event, leaving scrollOffsetRef stale.
        // Read the real DOM value before computing which rows to render.
        if (tableContainerRef.current) {
            scrollOffsetRef.current = tableContainerRef.current.scrollTop;
        }

        const visibleRows = Math.ceil(containerHeight / ROW_HEIGHT);
        const bufferRows = 10;
        const currentRow = Math.floor(scrollOffsetRef.current / ROW_HEIGHT);
        const start = Math.max(0, currentRow - bufferRows);
        const end = Math.min(allVisibleData.length, start + visibleRows + bufferRows * 2);
        const next = { start, end };
        prevRangeRef.current = next;
        setVisibleRange(prev =>
            prev.start === start && prev.end === end ? prev : next
        );
    }, [effectiveHeight, allVisibleData.length]);
   
    
    // Track scroll position for virtual scrolling.
    // The handler computes the visible range itself and only triggers a
    // state update when the range changes, so scrolling within the same
    // range produces zero re-renders.
    //
    // After a range change the spacer <tr> heights are recalculated with
    // the assumed 45px row height.  If actual rows differ even slightly,
    // the total content height shifts, the browser adjusts scrollTop to
    // compensate, and a new scroll event fires.  If that adjusted position
    // computes a *different* range, the cycle repeats → vibration.
    //
    // `settlingRef` breaks this: after every range change we skip the
    // very next scroll-event RAF so the DOM can settle.
    const settlingRef = useRef(false);
    const prevRangeRef = useRef({ start: 0, end: 0 });

    useEffect(() => {
        if (!effectiveHeight) return;
        
        const container = tableContainerRef.current;
        if (!container) return;

        const containerHeight = parseInt(effectiveHeight);
        const visibleRows = Math.ceil(containerHeight / ROW_HEIGHT);
        const bufferRows = 10;
        
        let rafId = null;
        
        const handleScroll = (e) => {
            if (e.target !== container) return;
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const newOffset = Math.floor(e.target.scrollTop);

                // After a range change the browser fires a tiny compensating scroll
                // caused by spacer height adjustment.  Skip it so the layout can
                // settle and we don't oscillate.  BUT only skip if the movement is
                // sub-row (< ROW_HEIGHT) — a larger delta is real user scrolling
                // and must never be dropped, otherwise fast scrolling shows gray.
                if (settlingRef.current) {
                    settlingRef.current = false;
                    if (Math.abs(newOffset - scrollOffsetRef.current) < ROW_HEIGHT) {
                        rafId = null;
                        return;
                    }
                    // Large delta → fall through and process as a normal scroll event.
                }
                scrollOffsetRef.current = newOffset;

                const dataLength = allVisibleDataLengthRef.current;
                const currentRow = Math.floor(newOffset / ROW_HEIGHT);
                const start = Math.max(0, currentRow - bufferRows);
                const end = Math.min(dataLength, start + visibleRows + bufferRows * 2);

                const prev = prevRangeRef.current;
                if (prev.start !== start || prev.end !== end) {
                    prevRangeRef.current = { start, end };
                    settlingRef.current = true;
                    setVisibleRange({ start, end });
                }
                rafId = null;
            });
        };
        
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [effectiveHeight]);

    // Bubble up the filtered (but uncustomFilteredDatad) rows — guard identity to avoid loops
    const lastCustomFilteredRef = useRef(customFilteredData);
    useLayoutEffect(() => {

        setFilteredRowIds( new Set(getLeafDescendantIds({items: customFilteredData})));
        
        if (typeof onFilteredDataChange !== "function") return;
        const prev = lastCustomFilteredRef.current;
        // cheap equality: same ref or same length
        if (prev === customFilteredData || prev.length === customFilteredData.length) {
            lastCustomFilteredRef.current = customFilteredData;
            return;
        }
        lastCustomFilteredRef.current = customFilteredData;
        onFilteredDataChange(customFilteredData);
    }, [customFilteredData, onFilteredDataChange, data, sortedData, searchFilteredData, filteredData]);

    // Handler for filter changes
    const handleFilterChange = (filter, value) => {
        // Only apply field-based filtering for filters without a customIncludeFunction.
        // Custom filters are applied separately via filterValues.
        if (typeof filter.customIncludeFunction !== "function") {
            if (value === undefined || value === null) {
                setFilter(filter.id, "");
            } else {
                setFilter(filter.id, value);
            }
        }

        // If this filter uses a customIncludeFunction, update filterValues so the
        // custom filter re-runs with the new value (filterValues is not otherwise updated after init)
        if (typeof filter.customIncludeFunction === "function") {
            setFilterValues((prev) =>
                prev.map((fv) => fv.id === filter.id ? { ...fv, value } : fv)
            );
        }

        if (onFilterChange) onFilterChange({ filterId: filter.id, value: value });
    };

    // Handler for search input change
    const onSearchChanged = (e) => {
        setSearch(e.target.value);
    }

    const formattedFooterCell = (col, value, footer, fixedStyle = {}) => {
        // Note that I based this function on renderRow render logic for cells.
        // This hopefully means it will look similar to the rest of the table.
        // If we ever change that, we may need to update this too.
        // Also note that I removed the inner DIV, as it was interfering with ability to put a border above the footer row

        let innerContent;
        
        if (col.showInFooter === false) {
            // No footer required for this column
            innerContent = <span>&nbsp;</span>
        } else if (col.renderFooter) {
            // There's a specific footer render function for this column
            innerContent = <div 
                style={{ width: "100%" }} 
                className={col.align === "right" ? "text-end" : ""} >
                    {col.renderFooter(value, footer, data)}
            </div>               
        } else if (value === undefined) {
            // No footer value and no custom render function
            innerContent = <span>&nbsp;</span>
        } else {
            // No render function available for this column, so use default formatting based on data type
            switch (col.dataType) {
                case 'currency':
                    innerContent = (
                        <InputGroup>
                            <div style={{ width: "100%" }}>
                                <InputGroup className="text-end">
                                    <InputGroupText>
                                        £
                                    </InputGroupText>
                                    <Input
                                        id={col.key + "-footer"}
                                        disabled={true}
                                        value={formatCurrency(value)}
                                        type="text"
                                        className="text-end"
                                    />
                                </InputGroup>
                            </div>
                        </InputGroup>
                    )
                    break;
                case 'percentage':
                    innerContent = (
                        <InputGroup>
                            <div style={{ width: "100%" }}>
                                <InputGroup className="text-end">
                                    <Input
                                        id={col.key + "-footer"}
                                        disabled={true}
                                        className={"text-end"}
                                        type="text"
                                        value={formatNumber(value, 2)}
                                    />
                                    <InputGroupText>
                                        %
                                    </InputGroupText>
                                </InputGroup>
                            </div>
                        </InputGroup>
                    )
                    break;
                case "number":
                    innerContent = (
                        <InputGroup>
                            <div style={{ width: "100%" }}>
                                <InputGroup className="text-end">
                                    <Input
                                        id={col.key + "-footer"}
                                        disabled={true}
                                        className={"text-end"}
                                        type="text"
                                        value={formatNumber(value, 0)}
                                    />
                                </InputGroup>
                            </div>
                        </InputGroup>
                    )
                    break;
                default:
                    innerContent = (
                        <div 
                            style={{ width: "100%" }} 
                            className={col.align === "right" ? "text-end" : ""} >
                                {value}
                        </div>
                    )
                    break;
            }
        }

        // Apply solid background color to all footer cells
        const baseFooterBg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff";
        const footerBg = col.isFixed ? undefined : applySolidHeaderFooterColor(baseFooterBg);
        
        return (
            <td
                key={col.key}
                className={col.className || ""}
                style={{
                    ...col.style || {}, 
                    fontWeight: "bold", // NOTE THAT THIS IS DIFFERENT - MAKE FOOTER BOLD
                    background: footerBg || fixedStyle.background || (theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff"),
                    borderBottom: 'none',
                    // Strong top border to clearly separate footer from body
                    borderTop: "1px solid var(--bg-color-contrast2)",
                    ...fixedStyle
                }}
            >
                <div
                    className="d-flex align-items-center"
                    style={{
                        paddingLeft: "0rem",
                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                        width: "100%"
                    }}
                >
                    {innerContent}
                </div>
            </td>
        )
    }     

    const autogeneratedFooter = useMemo(() => {
        if (!showAutogeneratedFooter) return null;

        const autogenFooterData = {};

        // Work out the sum of any columns that can be totalled
        columns.forEach(col => {
            if (col.canBeTotalled) {
                autogenFooterData[col.key] = leafItems.filter(x => !x.disabled).reduce((sum, row) => { 
                    if (row.items && Array.isArray(row.items) && row.items.length > 0) {
                        const itemsTotal = row.items.reduce((subSum, item) => {
                            const runningTotal = parseFloat(item[col.key] || 0) + subSum;
                            return runningTotal
                        }, 0);
                        return itemsTotal + sum;
                    } else {
                        return parseFloat(row[col.key] || 0) + sum;
                    }
                }, 0);
            }
        });

        // If the column definition has a function to calculate the column, then use it
        columns.forEach(col => {
            if (col.calcFooter) {
                // There's a footer-specific calculation function
                autogenFooterData[col.key] = col.calcFooter(autogenFooterData, data)
            }
        });

        const footerInUseColumns = []

        // Using the calculated footer data, format the footer cells for all visible columns
        visibleColumns.forEach((col) => {
            // Insert spacer before first right-fixed column
            if (col.key === rightFixedEdgeKey) {
                footerInUseColumns.push(
                    <td key="__spacer-footer" style={{ padding: 0, border: "none" }} />
                );
            }
            const fixedStyle = getFixedStyle(col, -1); // -1 for non-striped footer
            const formattedCell = formattedFooterCell(col, autogenFooterData[col.key], autogenFooterData, fixedStyle);

            footerInUseColumns.push(formattedCell);
        });

        // If no right-fixed columns, add spacer at the end
        if (rightFixedKeys.size === 0) {
            footerInUseColumns.push(
                <td key="__spacer-footer" style={{ padding: 0, border: "none" }} />
            );
        }

        return footerInUseColumns;        
    }, [customFilteredData, columns, showAutogeneratedFooter, visibleColumns, theme, rightFixedEdgeKey, rightFixedKeys]);

    const visibleColumnGroups = useMemo(() => {    
        return Array.from(new Set(visibleColumns.map(col => col.group ?? "")));
    }, [visibleColumns]);

    const columnGroups = useMemo(() => {    
        return Array.from(new Set(columns.map(col => col.group ?? "")));
    }, [columns]);    

    const isLastColInGroup = (colKey) => {
        const col = visibleColumns.find(c => c.key === colKey);
        if (!col) return false;

        if (visibleColumns[visibleColumns.length - 1].key === colKey) return true; // Always true for last column
        
        // Filter to match the same columns being rendered (excluding groupHeader columns)
        const renderedColumns = visibleColumns.filter(x => !x.groupHeader);
        const groupCols = renderedColumns.filter(c => c.group === col.group);
        return groupCols[groupCols.length - 1].key === colKey;
    }

    const isFirstColInGroup = (colKey) => {
        const col = visibleColumns.find(c => c.key === colKey);
        if (!col) return false;

        if (visibleColumns[0].key === colKey) return true; // Always true for first column
        
        // Filter to match the same columns being rendered (excluding groupHeader columns)
        const renderedColumns = visibleColumns.filter(x => !x.groupHeader);
        const groupCols = renderedColumns.filter(c => c.group === col.group);
        return groupCols[0].key === colKey;
    }

    const toggleRow = useCallback((id) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                // Collapse this row and also clear any expanded descendants,
                // otherwise re-expanding the parent would immediately re-show
                // previously-expanded children (causing apparent row duplication).
                newSet.delete(id);
                const prefix = id + '-';
                for (const key of newSet) {
                    if (key.startsWith(prefix)) {
                        newSet.delete(key);
                    }
                }
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);


    const renderRow = useCallback((item, level = 0, parentKey = '', rowIndex = 0) => {
        const hasChildren = item.__hasChildren ?? (Array.isArray(item.items) && item.items.length > 0);
        const rowKey = item.__rowKey ?? `${parentKey}-${item.id}`;
        const isExpanded = hasChildren && expandedRows.has(rowKey);
        const rowIsDisabled = item.disabled === true;
        const disabledOpacity = rowIsDisabled ? (theme === "dark-mode" ? 0.25 : 0.5) : 1;

        // Calculate background color based on actual row index for consistent striping
        // Match Bootstrap striping: even indices (0,2,4...) get striped color
        const rowBgColor = rowIndex % 2 === 0 
            ? "var(--bg-color-contrast2)" 
            : "var(--bg-color-contrast1)";

        const row = (
            <tr
                key={rowKey}
                className={onRowClick ? "cursor-pointer" : ""}
                onClick={() => onRowClick?.(item)}
                style={{
                    ...(onRowClick ? { cursor: "pointer" } : {}),
                    backgroundColor: rowBgColor
                }}
            >
                {/* GroupHeader will span any columns with groupColumn set to true */}
                {visibleColumns.filter(col => col.groupHeader && hasChildren).map((col, colIndex) => {
                    const value = col.calcColumn ? col.calcColumn(item) : item[col.key];
                    const fixedStyle = getFixedStyle(col, rowIndex);
                    
                    const borderStyle = col.isFixed ? {
                        borderLeft: '3px solid var(--border-color)',
                        borderRight: '3px solid var(--border-color)'
                    } : {};

                    return (
                        <td
                            key={col.key}
                            className={col.className || ""}
                            style={{...(col.style || {}), ...fixedStyle, ...borderStyle, background: fixedStyle.background || rowBgColor, textDecoration: rowIsDisabled ? "line-through" : "none", opacity: !col.isFixed ? disabledOpacity : 1}}
                            colSpan={visibleColumns.filter(c => c.groupColumn).length || 1}
                        >
                            <div
                                className="d-flex align-items-center"
                                style={{ paddingLeft: `${colIndex === 0 ? level * 1.5 : 0}rem` }}
                            >
                                {colIndex === 0 && hasChildren && (
                                    <i
                                        className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRow(rowKey);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    />
                                )}
                                {col.render ? col.render(value, item, isExpanded, hasChildren) : (value ?? '')}
                            </div>
                        </td>
                    )
                })}
                {/* Group columns */}
                {visibleColumns.filter(col => col.groupColumn).map((col, colIndex) => {
                    if (hasChildren) return null; // Skip rendering group columns for header rows, as the header already is spanning the space that these take up

                    const value = item[col.key];
                    const fixedStyle = getFixedStyle(col, rowIndex);
                    
                    return (
                        <td
                            key={col.key}
                            className={col.className || ""}
                            style={{...(col.style || {}), ...fixedStyle, background: fixedStyle.background || rowBgColor, textDecoration: rowIsDisabled ? "line-through" : "none", opacity: !col.isFixed ? disabledOpacity : 1}}
                        >
                            <div
                                className="d-flex align-items-center"
                                style={{ paddingLeft: `${colIndex === 0 ? level * 1.5 : 0}rem` }}
                            >
                                {col.render ? col.render(value, item, isExpanded, hasChildren) : (value ?? '')}
                            </div>
                        </td>
                    );
                })}
                {/* Non-group columns */}
                {visibleColumns.filter(col => !col.groupHeader && !col.groupColumn).map((col, colIndex) => {
                    const value = col.calcColumn ? col.calcColumn(item) : item[col.key];
                    const fixedStyle = getFixedStyle(col, rowIndex);
                    const isFirstScrollable = !col.isFixed && scrollableColumns[0]?.key === col.key;
                    
                    return (
                        <React.Fragment key={`body-${col.key}`}>
                        {col.key === rightFixedEdgeKey && <td key="__spacer" style={{ padding: 0, border: "none", background: rowBgColor }} />}
                        <td
                            key={col.key}
                            className={col.className || ""}
                            style={{...(col.style || {}), ...fixedStyle, background: fixedStyle.background || rowBgColor, textDecoration: rowIsDisabled && col.key !== "actions" ? "line-through" : "none", opacity: !col.isFixed ? disabledOpacity : 1, ...(isFirstScrollable ? { paddingLeft: "10px" } : {})}}
                        >
                            <div
                                className={`d-flex align-items-center ${col.align === "right" ? "text-end" : ""} ${lockState?.isLocked && (level === 0 && !hasChildren) ? "locked" : ""}`}
                                style={{
                                    paddingLeft: `${colIndex === 0 ? level * 1.5 : 0}rem`,
                                    justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                    width: "100%"
                                }}
                            >
                                {colIndex === 0 && hasChildren && visibleColumns.filter(col => col.groupHeader).length === 0 && (
                                    <i
                                        className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRow(rowKey);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    />
                                )}
                                {col.render ? col.render(value, item, isExpanded, hasChildren) : (value ?? '')}
                                {hasChildren && col.key === "description" && (
                                    <span className="ms-2 text-muted small text-nowrap flex-shrink-0">
                                        {`(${item.items.length} item${item.items.length > 1 ? "s" : ""})`} 
                                    </span>
                                )}                                
                            </div>
                        </td>
                        </React.Fragment>
                    );
                })}
                {rightFixedKeys.size === 0 && <td key="__spacer" style={{ padding: 0, border: "none", background: rowBgColor }} />}
            </tr>
        );

        return row;
    }, [visibleColumns, expandedRows, toggleRow, onRowClick, getFixedStyle, lockState, theme, rightFixedEdgeKey, rightFixedKeys]);

    // Rendered rows with virtual scrolling
    const renderedRows = useMemo(() => {
        const rows = [];
        const rowHeight = 45;
        
        // Add spacer before visible rows
        if (effectiveHeight && visibleRange.start > 0) {
            const spacerHeight = Math.floor(visibleRange.start * rowHeight);
            const renderedColCount = visibleColumns.filter(x => !x.groupHeader).length;
            rows.push(
                <tr key="spacer-top" style={{ height: `${spacerHeight}px`, lineHeight: 0 }}>
                    <td colSpan={renderedColCount + 1} style={{ padding: 0, border: 'none', height: `${spacerHeight}px` }} />
                </tr>
            );
        }
        
        // Render visible rows
        for (let i = visibleRange.start; i < visibleRange.end; i++) {
            const item = allVisibleData[i];
            if (!item) continue;
            
            rows.push(renderRow(item, item.__level, item.__parentKey, i));
        }
        
        // Add spacer after visible rows
        if (effectiveHeight && visibleRange.end < allVisibleData.length) {
            const spacerHeight = Math.floor((allVisibleData.length - visibleRange.end) * rowHeight);
            const renderedColCount = visibleColumns.filter(x => !x.groupHeader).length;
            rows.push(
                <tr key="spacer-bottom" style={{ height: `${spacerHeight}px`, lineHeight: 0 }}>
                    <td colSpan={renderedColCount + 1} style={{ padding: 0, border: 'none', height: `${spacerHeight}px` }} />
                </tr>
            );
        }

        return rows;
    }, [allVisibleData, visibleRange, renderRow, visibleColumns.length, requestedHeight]);




    //nested
    const setAllRowsExpandedState = (setToExpand) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);

            for (const id of data.map(item => item.id)) {
                //The code expects the id to be parentid then hyphen then id of the item, but, as this is a top level row, we just add a hyphen before the id
                const rowKey = `-${id}`;

                if (setToExpand && !newSet.has(rowKey)) {
                    newSet.add(rowKey); // 
                } else if (!setToExpand && newSet.has(rowKey)) {
                    newSet.delete(rowKey);
                }
            }
            return newSet;
        });
    };

    const colIsVisible = (col) => {
        return ((visibleColumnsAsObject ?? {})[col.key] || currentColumnProfile?.find(x => x.key === col.key)?.isRequired)
    }

    // only pinLocked items (or legacy description column) is non-toggleable.
    const isPinLocked = (col) => col?.pinLocked === true || col?.key === "description";

    if (error) return <DataLoaderFailed />;

    return (
        <>
            <div
                ref={componentRootRef}
                style={{
                    position: "relative",
                    ...(fillContainerHeight && !isFullscreen
                        ? { height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }
                        : {}),
                }}
            >{(isUpdating || isLoading) && (
                <div style={{
                    position: isFullscreen ? "fixed" : "absolute",
                    inset: 0,
                    zIndex: isFullscreen ? 1050 : 200,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: theme === "dark-mode" ? "rgba(27,39,45,0.75)" : "rgba(255,255,255,0.75)",
                    backdropFilter: "blur(2px)",
                    borderRadius: "inherit",
                    pointerEvents: "all",
                    color: theme === "dark-mode" ? "#fff" : "#212529",
                }}>
                    <Spinner color="primary" />
                    <div className="mt-2 fw-bold">{isUpdating ? "Updating…" : "Loading…"}</div>
                </div>
            )}
            <div style={{
                ...(isFullscreen ? {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1040,
                    backgroundColor: theme === "dark-mode" ? "var(--bg-color)" : "#fff",
                    padding: "1rem",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column"
                } : {}),
                ...(fillContainerHeight && !isFullscreen
                    ? { flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0 }
                    : {})
            }}>
            {/* Search Bar */}
            {(searchableFields.length > 0 || actions.length > 0) && (
                <Container fluid className="px-0" style={isFullscreen ? { flexShrink: 0 } : {}}>
                    <div
                        className="d-flex justify-content-between align-items-center flex-wrap gap-3"
                        style={{ minHeight: "40px" }}
                    >
                        {searchableFields.length > 0 && (
                            <Form
                                style={{ flex: "1 1 auto", minWidth: "250px" }}
                                onSubmit={(e) => e.preventDefault()}
                            >
                                <Label className="visually-hidden" htmlFor="search">
                                    Search
                                </Label>
                                <Input
                                    type="search"
                                    id="search"
                                    disabled={listOfFieldsBeingSearched === ""}
                                    placeholder={listOfFieldsBeingSearched !== "" ? `Search by ${listOfFieldsBeingSearched}` : "Please select search fields to enable searching..."}
                                    onChange={onSearchChanged}
                                    style={{ height: "38px" }}
                                />
                            </Form>
                        )}
                        {searchableFields.length > 0 && (
                            <>
                                <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                                    <DropdownToggle caret outline color={theme === "dark-mode" ? "dark" : "light"} style={{textAlign: "left" }} className="text-truncate">
                                        <i className="bi bi-funnel me-2"></i> Search Fields
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem toggle={false} onClick={(e) => e.stopPropagation()}>
                                            <FormGroup check className="mb-0">
                                                <SelectAllCheckbox
                                                    checked={allSearchFieldsChecked}
                                                    indeterminate={searchFieldsIndeterminate}
                                                    onChange={handleSelectAllSearchFieldsChange}
                                                    id={`${instancePrefix}-search-field-select-all`}
                                                />
                                                <Label check htmlFor={`${instancePrefix}-search-field-select-all`}>(Select All)</Label>
                                            </FormGroup>
                                        </DropdownItem>
                                        <DropdownItem divider />
                                        {Object.keys(selectedSearchKeys)
                                            .filter(key => inUseSearchKeys.includes(key))
                                            .sort((a, b) => { a.localeCompare(b) })
                                            .map ( (itemKey) => (
                                            <DropdownItem key={itemKey} toggle={false} onClick={(e) => e.stopPropagation()}>
                                                <FormGroup check className="mb-0">
                                                    <Input
                                                        type="checkbox"
                                                        id={`${instancePrefix}-search-field-${itemKey}`}
                                                        checked={selectedSearchKeys[itemKey]}
                                                        onChange={() => handleCheckboxChange(itemKey)}
                                                    />
                                                    <Label check htmlFor={`${instancePrefix}-search-field-${itemKey}`}>{visibleColumns.find(x => x.key === itemKey)?.label || itemKey}</Label>
                                                </FormGroup>
                                            </DropdownItem>
                                        )
                                        )}
                                    </DropdownMenu>
                                </Dropdown>                                                                
                            </>
                        )}
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            {filters
                                .filter((filter) => !filter.advanced)
                                .map((filter, index) => {
                                    return (
                                        <DataFilter
                                            key={index}
                                            id={filter.id}
                                            label={filter.label}
                                            type={filter.type}
                                            placeholder={filter.placeholder}
                                            isSearchable={filter.isSearchable}
                                            options={filter.options}
                                            value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                            onChange={(value) => handleFilterChange(filter, value)}
                                            isInvalid={filter.isInvalid}
                                            defaultValue={filter.defaultValue}
                                        />)
                                }
                                )
                            }
                        </div>

                        {/* Advanced Filters Toggle */}
                        {filters.filter((filter) => filter.advanced && !filter.isDateTimeRange).length > 0 && (
                            <Button
                                outline={!advancedFiltersOpen}
                                color={theme === "dark-mode" ? "dark" : "light"}
                                onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
                            >
                                <i className="bi bi-funnel"></i>
                            </Button>
                        )}

                        <div style={{ flexGrow: 1 }}></div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-2">
                            {actions.map((action, index) => (
                                <span key={index}>{action}</span>
                            ))}
                            {exportToCsv && (<Download fileName={csvFileName} data={customFilteredData} showIcon={false} showTooltip={true} />)}
                            {allowColumnCustomisation && (
                                <div className="d-flex gap-2">
                                    <Button outline color={theme === "dark-mode" ? "dark" : "light"} onClick={toggleFullscreen} title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}>
                                        <i className={`bi ${isFullscreen ? "bi-fullscreen-exit" : "bi-fullscreen"} fs-5`}></i>
                                    </Button>
                                    <Button outline color={theme === "dark-mode" ? "dark" : "light"} onClick={toggleSettingsModal}>
                                        <i className="bi bi-gear fs-5"></i>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </Container>
            )}

            <div className="ms-1" style={{ fontSize: "0.875rem", color: theme === "dark-mode" ? "var(--text-color-secondary)" : "var(--text-color-secondary)", ...(isFullscreen ? { flexShrink: 0 } : {}) }}>
                {numRecords} records, {numFilteredRecords} filtered{allowMultiSelect ? `, ${selectedInFilteredCount} selected` : ""}
            </div>

            {/* Advanced Filters */}
            {advancedFiltersOpen && (
                <>
                    <br />
                    <Row>
                        <Container
                            fluid
                            style={advancedFiltersOpen ? mountedStyle : unmountedStyle}
                            className="contrast-dropdown py-3 transitionDiv"
                        >
                            <div style={{ minHeight: "40px" }}>
                                {filters
                                    .filter((filter) => filter.advanced)
                                    .map((filter, index) => {
                                        return (
                                            <DataFilter
                                                key={index}
                                                id={filter.id}
                                                label={filter.label}
                                                type={filter.type}
                                                placeholder={filter.placeholder}
                                                isSearchable={filter.isSearchable}
                                                options={filter.options}
                                                value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                                onChange={(value) => handleFilterChange(filter, value)}
                                                isInvalid={filter.isInvalid}
                                                defaultValue={filter.defaultValue}
                                                advanced={true}
                                            />
                                        )
                                    }
                                    )}
                            </div>
                        </Container>
                    </Row>
                </>
            )}

            <div className="px-0" style={{ 
                width: "100%", 
                maxWidth: "100%", 
                overflow: "hidden",
                ...(isFullscreen || fillContainerHeight ? { flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0 } : {})
            }}>
                <div style={{ 
                    display: (requestedHeight || isFullscreen || fillContainerHeight) ? "flex" : "block",
                    flexDirection: (requestedHeight || isFullscreen || fillContainerHeight) ? "column" : undefined,
                    position: "relative", 
                    width: "100%", 
                    maxWidth: "100%", 
                    minWidth: 0, 
                    overflow: "hidden",
                    boxSizing: "border-box",
                    paddingRight: hasVerticalScrollbar ? 0 : "20px",
                    paddingBottom: hasHorizontalScrollbar ? 0 : "20px",
                    ...(isFullscreen || fillContainerHeight ? { flexGrow: 1, minHeight: 0 } : {})
                }}>
                    <style>
                        {`
                            .custom-scrollbar::-webkit-scrollbar {
                                width: 20px;
                                height: 20px;
                            }
                            .custom-scrollbar::-webkit-scrollbar-track {
                                background: ${theme === "dark-mode" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"};
                                border-radius: 10px;
                                border: 4px solid transparent;
                                background-clip: padding-box;
                            }
                            .custom-scrollbar::-webkit-scrollbar-thumb {
                                background: ${theme === "dark-mode" ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)"};
                                border-radius: 10px;
                                border: 4px solid transparent;
                                background-clip: padding-box;
                            }
                            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                                background: ${theme === "dark-mode" ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)"};
                                border-radius: 10px;
                                border: 4px solid transparent;
                                background-clip: padding-box;
                            }
                            .custom-scrollbar::-webkit-scrollbar-corner {
                                background: transparent;
                            }
                        `}
                    </style>
                    <div 
                        ref={tableContainerRef} 
                        className="custom-scrollbar"
                        style={{
                            overflowY: (effectiveHeight || isFullscreen) ? "auto" : "visible",
                            overflowX: "auto",
                            position: "relative", 
                            width: "100%", 
                            height: isFullscreen ? "100%" : (effectiveHeight || 'auto'),
                            maxHeight: (!isFullscreen && shrinkHeightToContents && requestedHeight) ? requestedHeight : undefined,
                            maxWidth: "100%", 
                            minWidth: 0,
                            marginTop: "1rem"
                        }}
                    >
                        <Table
                            innerRef={tableRef}
                            className={
                                theme === "dark-mode"
                                    ? "no-wrap align-middle table-dark"
                                    : "no-wrap align-middle"
                            }
                            hover
                            style={{ 
                                display: "table",
                                tableLayout: "fixed",
                                width: "100%",
                                minWidth: `${totalTableWidth}px`,
                                marginBottom: 0,
                                borderCollapse: "separate",
                                borderSpacing: 0
                            }}
                        >
                            <colgroup>
                                {visibleColumns.filter(col => !col.groupHeader && !rightFixedKeys.has(col.key)).map(col => (
                                    <col key={col.key} style={{ width: `${getColWidth(col)}px` }} />
                                ))}
                                <col key="__spacer" />
                                {visibleColumns.filter(col => !col.groupHeader && rightFixedKeys.has(col.key)).map(col => (
                                    <col key={col.key} style={{ width: `${getColWidth(col)}px` }} />
                                ))}
                            </colgroup>
                            <thead style={{ 
                                position: 'sticky', 
                                top: 0, 
                                zIndex: 100,
                                background: theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff",
                                boxShadow: theme === "dark-mode" 
                                    ? "0 1px 0 0 rgba(255, 255, 255, 0.85)" 
                                    : "0 1px 0 0 rgba(0, 0, 0, 0.15)"
                            }}>
                        {visibleColumnGroups.filter(x => x !== "").length > 0 && showGroupHeaders && (
                            <tr>
                                {visibleColumns.map((col, idx) => {
                                    // Determine fixed type: 'left', 'right', or 'none'
                                    const getFixedType = (column) => {
                                        if (leftFixedPositions.has(column.key)) return 'left';
                                        if (rightFixedKeys.has(column.key)) return 'right';
                                        return 'none';
                                    };
                                    
                                    const currentGroup = col.group ?? "";
                                    const currentFixedType = getFixedType(col);
                                    
                                    // Check if this is the first column in a group segment
                                    const isFirstInSegment = idx === 0 || 
                                        (visibleColumns[idx - 1].group ?? "") !== currentGroup ||
                                        getFixedType(visibleColumns[idx - 1]) !== currentFixedType;
                                    
                                    if (!isFirstInSegment) return null; // Skip non-first columns in segment
                                    
                                    // Count consecutive columns in same group with same fixed type
                                    let colSpan = 1;
                                    
                                    for (let i = idx + 1; i < visibleColumns.length; i++) {
                                        const nextCol = visibleColumns[i];
                                        const nextGroup = nextCol.group ?? "";
                                        const nextFixedType = getFixedType(nextCol);
                                        
                                        // Stop if group changes or fixed type changes
                                        if (nextGroup !== currentGroup || nextFixedType !== currentFixedType) {
                                            break;
                                        }
                                        colSpan++;
                                    }
                                    
                                    // Use the LAST column in the segment for fixedStyle, so edge columns get their shadow/border
                                    const lastColInSegment = visibleColumns[idx + colSpan - 1];
                                    const fixedStyle = getFixedStyle(col, -1);
                                    // Group headers can span multiple columns, so they must not inherit
                                    // the fixed single-column width/minWidth from getFixedStyle.
                                    const { width: _groupWidth, minWidth: _groupMinWidth, ...groupFixedStyle } = fixedStyle;
                                    const lastColFixedStyle = getFixedStyle(lastColInSegment, -1);

                                    // For right-fixed grouped headers, anchor to the segment's right-most column.
                                    // Using the first column's `right` (e.g. disabled column) shifts the group left.
                                    if (currentFixedType === 'right') {
                                        groupFixedStyle.right = lastColFixedStyle.right;
                                    }
                                    
                                    // Apply solid background to all header cells
                                    const baseHeaderBg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff";
                                    const headerBg = col.isFixed ? undefined : applySolidHeaderFooterColor(baseHeaderBg);
                                    
                                    const lightBorder = theme === "dark-mode" ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e8e8e8";

                                    const spacerBg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff";
                                    return (
                                        <React.Fragment key={`group-frag-${idx}`}>
                                            {col.key === rightFixedEdgeKey && <th key="__spacer-group" style={{ padding: 0, borderLeft: "none", borderRight: "none", borderTop: "none", borderBottom: lightBorder, background: spacerBg }} />}
                                            <th key={`${currentGroup}-${idx}`} colSpan={colSpan} style={{ position: "sticky", top: 0, ...groupFixedStyle, borderRight: lastColFixedStyle.borderRight || lightBorder, boxShadow: lastColFixedStyle.boxShadow, borderLeft: lastColFixedStyle.borderLeft, background: headerBg || fixedStyle.background, textAlign: "center", borderBottom: lightBorder }}>
                                                {currentGroup === "Properties" ? "" : currentGroup}
                                            </th>
                                        </React.Fragment>
                                    );
                                })}
                                {rightFixedKeys.size === 0 && (() => { const lb = theme === "dark-mode" ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e8e8e8"; const bg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff"; return <th key="__spacer-group" style={{ padding: 0, borderLeft: "none", borderRight: "none", borderTop: "none", borderBottom: lb, background: bg }} />; })()}
                            </tr>
                        )}
                        <tr>
                            {visibleColumns.filter(x => !x.groupHeader).map((col, index) => {
                                const isFirstScrollable = !col.isFixed && scrollableColumns[0]?.key === col.key;
                                const baseStyle = col.isSortDisabled
                                    ? { ...(col.style || {}), paddingLeft: isFirstScrollable ? "10px" : "0" }
                                    : {
                                        cursor: "pointer",
                                        ...(col.style || {}),
                                        paddingLeft: (isFirstScrollable || isFirstColInGroup(col.key)) ? "10px" : "0",
                                        borderRight: (isLastColInGroup(col.key) ? (theme === "dark-mode" ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e8e8e8") : undefined),
                                        // Bottom border to separate header from body
                                        borderBottom: theme === "dark-mode" ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e8e8e8"
                                    };
                                const fixedStyle = getFixedStyle(col, -1); // -1 for non-striped header
                                
                                // Apply solid background to all header cells
                                const baseHeaderBg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff";
                                const headerBg = col.isFixed ? undefined : applySolidHeaderFooterColor(baseHeaderBg);
                                
                                const spacerBorderBottom = theme === "dark-mode" ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e8e8e8";
                                const spacerHeaderBg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff";
                                return (
                                <React.Fragment key={`hdr-${col.key}`}>
                                {col.key === rightFixedEdgeKey && <th key="__spacer-hdr" style={{ padding: 0, borderLeft: "none", borderRight: "none", borderTop: "none", borderBottom: spacerBorderBottom, background: spacerHeaderBg }} />}
                                <th key={col.key}
                                    className={col.className || ""}
                                    style={{ 
                                        ...(col.isFixed ? { position: "sticky", top: 0 } : {}),
                                        ...baseStyle, 
                                        ...fixedStyle, 
                                        background: headerBg || fixedStyle.background 
                                    }}
                                    onClick={() => !col.isSortDisabled && sortBy(col.key)}>
                                    <span style={{ 
                                        display: "flex",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        textAlign: col.align === "right" ? "right" : "left", // This is needed to ensure the header text aligns properly if the content wraps
                                        width: "100%"
                                    }}>
                                        {index === 0 && expandCollapseButtonState === "expand all" && (
                                            <i
                                                className={`bi ${"bi-chevron-double-down"} me-2`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAllRowsExpandedState(true);
                                                    setExpandCollapseButtonState("collapse all");
                                                }}
                                            />
                                        )}
                                        {index === 0 && expandCollapseButtonState === "collapse all" && (
                                            <i
                                                className={`bi ${"bi-chevron-double-up"} me-2`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAllRowsExpandedState(false);
                                                    setExpandCollapseButtonState("expand all");
                                                }}
                                            />
                                        )}
                                        <span className={col.align === "left" ? "ms-2" : ""}>{col.header ?? col.label}</span>
                                        &nbsp;
                                        {sortConfig?.key === col.key && !col.isSortDisabled && <SortIcon sort={sortConfig?.direction} />}
                                        {col.isSortDisabled && col.key !== "select" && <i style={{ opacity: 0.2 }} className="bi bi-chevron-expand"></i>}
                                    </span>
                                </th>
                                </React.Fragment>
                            )
                            })}
                            {rightFixedKeys.size === 0 && (() => { const bb = theme === "dark-mode" ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e8e8e8"; const bg = theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff"; return <th key="__spacer-hdr" style={{ padding: 0, borderLeft: "none", borderRight: "none", borderTop: "none", borderBottom: bb, background: bg }} />; })()}
                        </tr>
                    </thead>
                    <tbody ref={tbodyRef}>
                        {renderedRows}
                    </tbody>
                    {showAutogeneratedFooter && (
                        <tfoot style={{ 
                            position: 'sticky', 
                            bottom: -1, 
                            zIndex: 100,
                            background: theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff",
                            boxShadow: theme === "dark-mode" 
                                ? "0 -1px 0 0 rgba(255, 255, 255, 0.85)" 
                                : "0 -1px 0 0 rgba(0, 0, 0, 0.15)"
                        }}>
                            <tr style={{ borderBottom: 'none' }}>{autogeneratedFooter}</tr>
                        </tfoot>
                    )}
                    {footer && (
                        <tfoot style={{ 
                            position: 'sticky', 
                            bottom: -1, 
                            zIndex: 100,
                            background: theme === "dark-mode" ? "var(--bg-color-contrast1)" : "#fff",
                            boxShadow: theme === "dark-mode" 
                                ? "0 -1px 0 0 rgba(255, 255, 255, 0.85)" 
                                : "0 -1px 0 0 rgba(0, 0, 0, 0.15)"
                        }}>
                            <tr style={{ borderBottom: 'none' }}>{footer}</tr>
                        </tfoot>
                    )}
                        </Table>
                    </div>
                    {/* Scroll-to-origin button overlaying the scrollbar corner */}
                    {(effectiveHeight || isFullscreen) && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                if (tableContainerRef.current) {
                                    scrollOffsetRef.current = 0;
                                    settlingRef.current = false;
                                    tableContainerRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (tableContainerRef.current) {
                                        scrollOffsetRef.current = 0;
                                        settlingRef.current = false;
                                        tableContainerRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                                    }
                                }
                            }}
                            title="Scroll to top"
                            aria-label="Scroll to top"
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: '20px',
                                height: '20px',
                                zIndex: 110,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: theme === "dark-mode" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.03)",
                                borderRadius: '4px 0 0 0',
                                transition: 'background 0.2s',
                                boxSizing: 'border-box',
                                pointerEvents: 'auto',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = theme === "dark-mode" ? "rgba(255, 255, 255, 0.25)" : "rgba(0, 0, 0, 0.12)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = theme === "dark-mode" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.03)";
                            }}
                        >
                            <i className="bi bi-house-door" style={{ 
                                fontSize: '10px', 
                                opacity: 0.5,
                                color: theme === "dark-mode" ? "#fff" : "#000",
                                lineHeight: 1
                            }}></i>
                        </div>
                    )}
                </div>
            </div>

            <Modal isOpen={settingsModalOpen} toggle={toggleSettingsModal} scrollable centered backdrop="static" size="xl" style={{ maxWidth: "750px" }}>
                <ModalHeader toggle={toggleSettingsModal}>Table Settings</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col style={{ display: "inline-flex", alignItems: "center" }}>
                            {Array.from(new Set(columns
                                .filter(x => currentColumnProfile?.map(x => x.key).includes(x.key) && x.label !== "" && x.label !== undefined)                                
                                .map(x => x.group))).map((tab) => (
                                <h6
                                    key={tab}
                                    className={
                                        currentActiveColumnSettingsGroupTab === tab
                                            ? "custom-nav-selected custom-nav m-3"
                                            : "custom-nav m-3"
                                    }
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setCurrentActiveColumnSettingsGroupTab(tab)}
                                >
                                    {tab}
                                </h6>
                            ))}
                        </Col>
                    </Row>
                    {/* SCROLLABLE COLUMN LIST */}
                    <div className="mt-3 ms-3" style={{ minHeight: "400px", maxHeight: "800px", overflowY: "auto", paddingRight: "10px" }}>
                        {columns
                            .filter(x => currentColumnProfile?.map(x => x.key).includes(x.key) && x.hideFromSettingsDialog !== true)                                
                            .filter(x => x.group === currentActiveColumnSettingsGroupTab)
                            .sort((a, b) => a.label.localeCompare(b.label))
                            .map(col => (
                                <div key={col.key} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    <FormGroup switch style={{ flex: 1, marginBottom: 0, marginTop: 0 }}>
                                        <Input
                                            type="switch"
                                            role="switch"
                                            id={`switch-${col.key}`}
                                            disabled={currentColumnProfile?.find(x => x.key === col.key)?.isRequired}
                                            checked={colIsVisible(col)}
                                            onChange={(e) => {
                                                const updated = {
                                                    ...visibleColumnsAsObject,
                                                    [col.key]: e.target.checked,
                                                };
                                                setVisibleColumnsAsObject(updated);
                                                localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(updated));
                                            }}
                                        />
                                        <Label check htmlFor={`switch-${col.key}`}>{col.settingsLabel ?? col.label ?? "Action"}
                                            {currentColumnProfile?.find(x => x.key === col.key)?.isRequired &&
                                                <small className="text-muted"> (Required)</small>
                                            }
                                        </Label>
                                    </FormGroup>
                                    <Button
                                        color="link"
                                        size="sm"
                                        style={{ marginBottom: 0, padding: "0", visibility: colIsVisible(col) ? "visible" : "hidden", opacity: isPinLocked(col) ? 0.4 : 1, cursor: isPinLocked(col) ? "default" : "pointer" }}
                                        onClick={() => {
                                            if (isPinLocked(col)) return;
                                            const fixed = fixedColumnsAsObject ?? {};
                                            const updated = {
                                                ...fixed,
                                                [col.key]: !fixed[col.key],
                                            };
                                            setFixedColumnsAsObject(updated);
                                            localStorage.setItem(FIXED_COLUMNS_KEY, JSON.stringify(updated));
                                        }}
                                        title={isPinLocked(col) ? "Column pin is locked" : (fixedColumnsAsObject ?? {})[col.key] ? "Unpin column" : "Pin column"}
                                    >
                                        <i className={(fixedColumnsAsObject ?? {})[col.key] ? "bi bi-pin-angle-fill" : "bi bi-pin-angle"} style={{ fontSize: "1.2rem" }}></i>
                                    </Button>
                                </div>
                            ))}
                    </div>
                    <hr />
                    <Button
                        outline
                        color="danger"
                        size="sm"
                        onClick={() => {
                            const defaults = getDefaultVisibilityForProfile(currentProfileKey);
                            setVisibleColumnsAsObject(defaults);
                            localStorage.setItem(COLUMN_SETTINGS_KEY, JSON.stringify(defaults));
                            
                            // Reset fixed columns to original column definitions
                            const defaultFixed = columns.reduce((acc, col) => {
                                if (col.isFixed) {
                                    acc[col.key] = true;
                                }
                                return acc;
                            }, {});
                            setFixedColumnsAsObject(defaultFixed);
                            localStorage.setItem(FIXED_COLUMNS_KEY, JSON.stringify(defaultFixed));
                        }}
                    >
                        Reset {currentProfileKey} to Defaults
                    </Button>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleSettingsModal}>Close</Button>
                </ModalFooter>
            </Modal>
            </div>
            </div>
        </>
    );
};

export default DataTableWindowed;
